import { useEffect, useState } from "react";
import { Container, Col, Row } from "reactstrap";
import { BreadcrumbItem } from "reactstrap";
import { useHistory } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import {
  Mutation,
  Button,
  useSectionContext,
} from "@repoflow/components/section";
import { CancellableContent } from "@repoflow/components/content-cancel";
import { postForm } from "@repoflow/browser/file-form-client";
import * as widgets from "widgets/register";
import * as SiteFileQL from "queries/site-file";
import { KIND_CONFIG } from "@common/schema/site-file/kind";

export const Sidebar = () => null;

export const Indice = () => <BreadcrumbItem>Add</BreadcrumbItem>;

// AN UPLOAD IS THREE STEPS AND ONLY THE FIRST IS A MUTATION
//
//   1. `create` writes the row and answers with a presigned POST form. No bytes
//      have moved and the row reads "processing".
//   2. the browser posts that form to S3 itself — `postForm` below. This is why
//      the API hands back a form instead of accepting the file: relaying bytes
//      through a Lambda would cap the upload at the response payload limit and
//      pay for every megabyte twice.
//   3. bucketws' upload-handler sees the S3 event and fills in the real size,
//      status and derived metadata — on ITS row, not ours. `sync` copies them
//      across, and is fired here so that landing back on the listing shows the
//      file as it actually is rather than as "processing".
//
// Step 3 is a race this screen can lose: the handler is asynchronous, and a
// large file can finish posting before it has run. Losing it is harmless — the
// row stays "processing" until the details screen syncs it again — which is why
// nothing here waits or retries.

// The filename the person chose, without its extension.
//
// IT IS NOT ON THE WIDGET'S VALUE, AND THIS IS THE ONLY PLACE IT SURVIVES
//
// @repoflow/form-schema's `file` widget sets `{ file, hash, ext }` and nothing
// else — bucketws' own upload screen destructures exactly those three, because
// they are all its API takes. pagews' `SiteFileInputCreate` added a fourth,
// `name: String!`, since bucketws builds its object key from the fileId it
// mints and forgets what the file was called. Reading `values.file.name` for it
// yields `undefined`, Apollo drops the key, and the mutation dies in variable
// coercion with `Field "name" of required type "String!" was not provided` — a
// 500 before any resolver runs, so nothing in the API's logs names the upload.
//
// The name IS there, one level down, on the DOM File the widget wrapped: the
// widget's own button renders `value.file.name`. So it is derived here rather
// than added to the shared widget, which is a separate package four apps
// install and whose other caller has no use for the field.
//
// Split the same way FileUtils.getExtension does — last dot wins, so
// "holiday.photo.jpg" is "holiday.photo" — and left whole when there is no dot
// at all. A file with no extension cannot reach this function: the widget
// refuses it against the kind's allowlist before it ever becomes a value.
export const baseNameOf = (filename) => {
  const segs = String(filename || "").split(".");
  return segs.length <= 1 ? segs[0] : segs.slice(0, -1).join(".");
};

const usePhase = () => {
  const [phase, setPhase] = useState("init");
  return { phase, setPhase };
};

export const Content = ({
  match: {
    params: { siteId, kind },
  },
}) => {
  const { phase, setPhase } = usePhase();
  const history = useHistory();
  const client = useApolloClient();
  // The kind's listing, which is where the file now is. replace(), not
  // goBack(): where you land used to depend on how you reached the form.
  const { base } = useSectionContext();

  useEffect(() => {
    if (phase === "complete") {
      history.replace(base);
    }
  }, [phase, history, base]);

  return (
    <Mutation
      initial={{ file: null }}
      // `Mutation` spreads its section context into the render prop and passes
      // this through as `context`. The route's params are not on it — the
      // handler below is not a Route component — so the kind is handed over
      // explicitly rather than re-read from the URL.
      context={{ kind, phase }}
      target={{ $ref: "/Public/SiteFile/Create" }}
      mutation={SiteFileQL.create}
      update={SiteFileQL.update()}
      onMutate={({ handler, state: { values } }) => {
        const {
          file: { file, hash, ext },
        } = values;
        const name = baseNameOf(file && file.name);

        setPhase("loading");

        handler({
          variables: {
            siteId,
            kind,
            // `name` is the filename the person chose, and this service is the
            // only place it survives: bucketws' object key is built from the
            // fileId it mints, so nothing over there remembers what the file
            // was called.
            input: { name, hash, ext },
          },
        });
      }}
      onCompleted={(data, { state: { values: { file: { file } } } }) => {
        const {
          viewer: {
            account: {
              sites: {
                get: {
                  files: {
                    create: { form, file: row },
                  },
                },
              },
            },
          },
        } = data;

        postForm(form, file, {
          onSuccess: async () => {
            // Best-effort, and deliberately not awaited into the navigation's
            // critical path any further than this: the row exists and the bytes
            // are in S3 whatever this returns. A failure here leaves a file
            // reading "processing" on the listing, which the details screen
            // corrects on open.
            try {
              await client.mutate({
                mutation: SiteFileQL.sync,
                variables: { siteId, kind, fileId: row.fileId },
                update: SiteFileQL.update(),
              });
            } catch (e) {
              // Not surfaced. Nothing failed from the person's point of view —
              // their file uploaded — and an error toast for a refresh that
              // will happen anyway is noise on a successful action.
            }
            setPhase("complete");
          },
          onError: (error) => {
            // The row DOES exist by this point, unlike the onError below, so
            // this lands on the listing where the half-made file is visible and
            // can be removed. Silently staying on the form would leave a row
            // nobody knows about.
            setPhase("complete");
            if (
              error.responseText &&
              error.responseText.indexOf("EntityTooLarge") !== -1
            ) {
              Notify.error(
                <span>
                  That file is over the {KIND_CONFIG[kind].label.toLowerCase()}{" "}
                  size limit.
                </span>
              );
            } else {
              Notify.error(<span>Error during upload operation.</span>);
            }
          },
        });
      }}
      onError={(error) => {
        // The mutation failed, so no row was created and retrying is clean.
        // Without this the button keeps spinning, disabled, forever. The
        // postForm failure path above is different — the row exists by then —
        // which is why the two do different things.
        setPhase("init");
        Notify.error(error.toString());
      }}
    >
      {MutationHandler}
    </Mutation>
  );
};

const MutationHandler = ({
  target,
  state,
  schema,
  mutate,
  context: { kind, phase },
}) => {
  const { values, setValues } = state;

  const formProps = {
    schema,
    target,
    widgets,
    onSubmit: () => {
      mutate();
    },
    onChange: (vals) => {
      setValues(vals);
    },
    config: {
      "/Public/SiteFile/Create": {
        fields: [
          {
            property: "file",
            // The widget enforces the same allowlist and ceiling the BUCKET was
            // provisioned with, so a file it refuses is one S3 would have
            // refused too — a rejection before the upload rather than a raw XML
            // error after it. Both come from @common/schema/site-file/kind,
            // which is also what the API validates against and what the bucket
            // was created from.
            allowed: KIND_CONFIG[kind].exts,
            minFileSize: KIND_CONFIG[kind].minFileSize,
            maxFileSize: KIND_CONFIG[kind].maxFileSize,
          },
        ],
      },
    },
    values,
  };

  return (
    <CancellableContent>
      <Container className="mt-4">
        <Row>
          <Col>
            <Form {...formProps}>
              <Button type="submit" loading={phase === "loading"}>
                Upload
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </CancellableContent>
  );
};
